import {useCallback, useContext} from 'react';

import {ValidationContext} from 'src/contexts/validation-context';
import {useDateTime} from 'src/hooks/date-time/useDateTime';
import {useCommonDataHook} from 'src/hooks/use-common-data-hook';
import {getAccountConference, updateConference} from 'src/openapi/admin/apis/ConferenceApi';
import {useAppDispatch} from 'src/store/hooks';

import {
    CONFERENCE_NAME_CONTROL,
    CONFERENCE_NAME_REQUIRED_ERROR,
    CONFERENCE_NAME_REQUIRED_MESSAGE,

    END_DATE_CONTROL,
    END_DATE_REQUIRED_ERROR,
    END_DATE_REQUIRED_MESSAGE,
    LICENSE_END_DATE_BEFORE_START_DATE_ERROR,
    LICENSE_END_DATE_BEFORE_START_DATE_MESSAGE,

    START_DATE_CONTROL,
    START_DATE_REQUIRED_ERROR,
    START_DATE_REQUIRED_MESSAGE,
} from '../constants';

import type {UpdateConferenceRequest} from 'src/openapi/admin/models/UpdateConferenceRequest';
import type {SuccessPayload} from 'src/typedef';

type Props = {
    accountId: number,
    conferenceId: number,
};

export const useEditConference = ({accountId, conferenceId}: Props) => {
    const apiDispatch = useAppDispatch();
    const {addError, validationErrors, removeError} = useContext(ValidationContext);
    const {getLocalDate} = useDateTime();

    const getDispatchAction = useCallback(async (signal: AbortSignal) => {
        const response = await apiDispatch(getAccountConference({accountId}, {signal}));
        if (response.payload.success) {
            const payload: SuccessPayload<UpdateConferenceRequest> = {
                success: true,
                error: false,
                data: {
                    name: response.payload.data.name,
                    advertiseOnLoginTime: response.payload.data.advertiseOnLoginTime,
                    startDate: response.payload.data.startDate,
                    endDate: response.payload.data.endDate,
                },
            };
            return Promise.resolve({type: '', payload});
        } else {
            return Promise.reject(response.payload);
        }
    }, [apiDispatch, accountId]);

    const saveDispatchAction = useCallback((
        updateConferenceRequest: UpdateConferenceRequest,
        signal: AbortSignal,
    ) => apiDispatch(
        updateConference({accountId, conferenceId, updateConferenceRequest}, {signal}),
    ), [conferenceId, accountId, apiDispatch]);

    const editValidation = useCallback((data: UpdateConferenceRequest) => {
        if (data.startDate) {
            removeError(START_DATE_CONTROL, [START_DATE_REQUIRED_ERROR]);
        }

        if (data.endDate) {
            removeError(END_DATE_CONTROL, [END_DATE_REQUIRED_ERROR]);
        }

        if (getLocalDate(data.endDate) >= getLocalDate(data.startDate)) {
            removeError(END_DATE_CONTROL, [LICENSE_END_DATE_BEFORE_START_DATE_ERROR]);
        }

        if (data.name) {
            removeError(CONFERENCE_NAME_CONTROL, [CONFERENCE_NAME_REQUIRED_ERROR]);
        }
    }, [getLocalDate, removeError]);

    const saveValidation = useCallback((data: UpdateConferenceRequest) => {
        let valid = true;

        if (validationErrors.length) {
            valid = false;
        }

        if (!data.startDate) {
            addError({
                controlId: START_DATE_CONTROL,
                code: START_DATE_REQUIRED_ERROR,
                message: START_DATE_REQUIRED_MESSAGE,
            });
            valid = false;
        }

        if (!data.endDate) {
            addError({
                controlId: END_DATE_CONTROL,
                code: END_DATE_REQUIRED_ERROR,
                message: END_DATE_REQUIRED_MESSAGE,
            });
            valid = false;
        }

        if (getLocalDate(data.endDate) < getLocalDate(data.startDate)) {
            addError({
                controlId: END_DATE_CONTROL,
                code: LICENSE_END_DATE_BEFORE_START_DATE_ERROR,
                message: LICENSE_END_DATE_BEFORE_START_DATE_MESSAGE,
            });
            valid = false;
        }

        if (!data.name) {
            addError({
                controlId: CONFERENCE_NAME_CONTROL,
                code: CONFERENCE_NAME_REQUIRED_ERROR,
                message: CONFERENCE_NAME_REQUIRED_MESSAGE,
            });
        }

        return valid;
    }, [validationErrors, addError, getLocalDate]);

    return useCommonDataHook({
        initValue: {
            name: '',
            advertiseOnLoginTime: 0,
            startDate: '',
            endDate: '',
        },
        getDispatchAction,
        saveDispatchAction,
        editValidation,
        saveValidation,
    });
};
